import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import {
  Box, Drawer, AppBar, Toolbar, Typography, List, ListItemButton,
  ListItemIcon, ListItemText, IconButton, Avatar, Divider, Alert, Button,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Assignment as WorkIcon,
  CalendarMonth as CalendarIcon,
  TrendingUp as StrategyIcon,
  Image as ImageIcon,
  Psychology as ContextIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  AccessTime as FocusTimeIcon,
  Assessment as ReportsIcon,
  RecordVoiceOver as TranscriptIcon,
  Groups as EAWorkspaceIcon,
  SmartToy as PromptIcon,
  AdminPanelSettings as AdminIcon,
  Business as TenantsIcon,
  Inventory as ProductsIcon,
  Email as EmailIcon,
  BarChart as StatsIcon,
  Security as SecurityIcon,
  Merge as MergeIcon,
} from '@mui/icons-material';

const DRAWER_WIDTH = 260;

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  section?: string;
}

// Navigation for executives, EAs, and tenant admins
const tenantNavItems: NavItem[] = [
  // OPERATE
  { label: 'Dashboard', path: '/', icon: <DashboardIcon />, section: 'OPERATE' },
  { label: 'Focus Time', path: '/focus', icon: <FocusTimeIcon />, section: 'OPERATE' },
  { label: 'Work Items', path: '/work-items', icon: <WorkIcon />, section: 'MANAGE' },
  { label: 'Calendar', path: '/calendar', icon: <CalendarIcon />, section: 'MANAGE' },
  { label: 'Transcripts', path: '/transcripts', icon: <TranscriptIcon />, section: 'MANAGE' },
  { label: 'Strategic Drivers', path: '/strategy', icon: <StrategyIcon />, section: 'MANAGE' },
  { label: 'Image Capture', path: '/images', icon: <ImageIcon />, section: 'MANAGE' },
  { label: 'Context Engine', path: '/context', icon: <ContextIcon />, section: 'MANAGE' },
  // INSIGHT
  { label: 'Reports', path: '/reports', icon: <ReportsIcon />, section: 'INSIGHT' },
  // EA WORKSPACE (shown for EA roles only — handled below)
  { label: 'EA Workspace', path: '/ea-workspace', icon: <EAWorkspaceIcon />, section: 'EA WORKSPACE' },
  // CONFIGURE
  { label: 'Preferences', path: '/preferences', icon: <SettingsIcon />, section: 'CONFIGURE' },
  { label: 'Prompt Management', path: '/prompt-management', icon: <PromptIcon />, section: 'CONFIGURE' },
  { label: 'Admin', path: '/admin', icon: <AdminIcon />, section: 'CONFIGURE' },
];

// Navigation for platform administrators — completely different view
const platformNavItems: NavItem[] = [
  { label: 'Platform Overview', path: '/platform', icon: <StatsIcon />, section: 'PLATFORM' },
  { label: 'Tenants', path: '/platform/tenants', icon: <TenantsIcon />, section: 'PLATFORM' },
  { label: 'Products & Pricing', path: '/platform/products', icon: <ProductsIcon />, section: 'BILLING' },
  { label: 'Email Settings', path: '/platform/email', icon: <EmailIcon />, section: 'BILLING' },
  { label: 'Security & Diagnostics', path: '/platform/security', icon: <SecurityIcon />, section: 'SYSTEM' },
];

export default function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mfaBannerDismissed, setMfaBannerDismissed] = useState(false);

  const isPlatformAdmin = user?.role === 'PlatformAdmin';
  const isEaRole = user?.role === 'ExecutiveAssistant';
  const showMfaBanner = user && !(user as any).mfaEnabled && !mfaBannerDismissed;

  const displayName = user?.displayName || user?.email || 'User';

  // Filter nav items based on role
  let navItems: NavItem[];
  if (isPlatformAdmin) {
    navItems = platformNavItems;
  } else {
    navItems = tenantNavItems.filter(item => {
      // Hide EA Workspace from non-EA users
      if (item.section === 'EA WORKSPACE' && !isEaRole && user?.role !== 'Admin' && user?.role !== 'TenantAdmin') {
        return false;
      }
      // Hide Prompt Management and Admin from regular users
      if (item.path === '/prompt-management' && user?.role !== 'Admin' && user?.role !== 'TenantAdmin') {
        return false;
      }
      if (item.path === '/admin' && user?.role !== 'Admin' && user?.role !== 'TenantAdmin') {
        return false;
      }
      return true;
    });
  }

  const productName = isPlatformAdmin ? 'Cadence Platform' : 'Cadence';
  const tagline = isPlatformAdmin ? 'Platform Administration' : 'Executive Orchestration Platform';

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2.5, pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box sx={{
            width: 28, height: 28, borderRadius: 1.5,
            background: isPlatformAdmin
              ? 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)'
              : 'linear-gradient(135deg, #7C3AED 0%, #2563EB 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, fontWeight: 700, color: '#fff',
          }}>
            C
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', letterSpacing: '-0.02em', fontSize: '1.1rem' }}>
            {productName}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 4.5 }}>
          {tagline}
        </Typography>
      </Box>
      <Divider sx={{ mx: 2, mt: 1 }} />
      <List sx={{ flex: 1, px: 1.5, py: 1 }}>
        {(() => {
          let lastSection = '';
          return navItems.map((item) => {
            const showSection = item.section && item.section !== lastSection;
            if (item.section) lastSection = item.section;
            return (
              <React.Fragment key={item.path + item.label}>
                {showSection && (
                  <Typography
                    variant="overline"
                    sx={{ display: 'block', px: 1.5, pt: 2, pb: 0.5, color: 'text.secondary', fontSize: '0.65rem', letterSpacing: '0.1em' }}
                  >
                    {item.section}
                  </Typography>
                )}
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => { navigate(item.path); setMobileOpen(false); }}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    '&.Mui-selected': {
                      bgcolor: isPlatformAdmin ? 'rgba(220, 38, 38, 0.12)' : 'rgba(108, 155, 255, 0.12)',
                      '& .MuiListItemIcon-root': { color: isPlatformAdmin ? 'error.main' : 'primary.main' },
                      '& .MuiListItemText-primary': { color: isPlatformAdmin ? 'error.main' : 'primary.main', fontWeight: 600 },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
              </React.Fragment>
            );
          });
        })()}
      </List>
      <Divider sx={{ mx: 2 }} />
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar sx={{
          width: 32, height: 32, fontSize: '0.875rem',
          bgcolor: isPlatformAdmin ? 'error.dark' : 'primary.dark',
        }}>
          {displayName.charAt(0)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap sx={{ color: 'text.primary', fontWeight: 500 }}>
            {displayName}
          </Typography>
          <Typography variant="caption" noWrap sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
            {user?.role}
          </Typography>
        </Box>
        <IconButton size="small" onClick={logout} title="Sign out">
          <LogoutIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Mobile app bar */}
      <AppBar position="fixed" sx={{ display: { md: 'none' }, bgcolor: 'background.paper', boxShadow: 'none', borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar>
          <IconButton edge="start" onClick={() => setMobileOpen(!mobileOpen)}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700, color: isPlatformAdmin ? 'error.main' : 'primary.main' }}>
            {productName}
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: 0 }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: DRAWER_WIDTH, bgcolor: 'background.paper' } }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{ display: { xs: 'none', md: 'block' }, '& .MuiDrawer-paper': { width: DRAWER_WIDTH, bgcolor: 'background.paper', borderRight: 1, borderColor: 'divider' } }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box component="main" sx={{ flex: 1, p: 3, pt: { xs: 10, md: 3 }, maxWidth: '100%', overflow: 'auto' }}>
        {showMfaBanner && (
          <Alert
            severity={user?.role === 'PlatformAdmin' || user?.role === 'TenantAdmin' || user?.role === 'Admin' ? 'warning' : 'info'}
            sx={{ mb: 2, borderRadius: 2 }}
            action={
              <Box display="flex" gap={1}>
                <Button color="inherit" size="small" onClick={() => navigate('/preferences')}>
                  Set up MFA
                </Button>
                {user?.role !== 'PlatformAdmin' && user?.role !== 'TenantAdmin' && user?.role !== 'Admin' && (
                  <Button color="inherit" size="small" onClick={() => setMfaBannerDismissed(true)}>
                    Dismiss
                  </Button>
                )}
              </Box>
            }
          >
            {user?.role === 'PlatformAdmin' || user?.role === 'TenantAdmin' || user?.role === 'Admin'
              ? 'Multi-factor authentication is required for administrator accounts. Set up MFA now.'
              : 'Secure your account — set up multi-factor authentication to protect your data.'
            }
          </Alert>
        )}
        <Outlet />
      </Box>
    </Box>
  );
}
